import { ActionTabs, v2 } from '@fieldnation/platform-components';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Part,
  PartUsage,
  PartV3,
  PartsFormV3ModalProps,
  USAGE_CODES as USAGE_ENUMS,
} from './types.d';
import {
  convertPartDataToV1Payload, getWorkorderStatusId, USAGE_CODES, validateAdditionalFields,
} from './utils';
import { error, success } from '../legacy/flash-messenger';
import { createPartApi, updatePartApi } from './service';
import AddPart from './AddPart';
import Usage from './Usage';
import usePartFiles from './usePartFiles';

const { Modal } = v2;

enum TABS {
  DETAILS = 'Details',
  USAGE = 'Usage',
}

const initialStateNewPart: Part = {
  id: 0,
  externalId: '',
  externalNumber: '',
  description: '',
  returnable: '',
  trackingNumber: '',
  carrier: '',
  carrierOther: '',
  returnInstruction: '',
};

const initialStatePartUsageData: PartUsage = {
  usageCode: '',
  customerRetained: '',
  returnTracking: '',
  returnCarrier: '',
  returnCarrierOtherName: '',
  note: '',
};

const PartsFormV3Modal = ({
  partData,
  usageData,
  openEditModal = false,
  setOpenEditModal,
  partsCorrelationId,
  workOrderId = 0,
  workOrderProjectName = '',
  workOrderStatus = '',
  workOrderUserRole = '',
  usageCodes = [],
  isProvider = false,
  addPart = false,
  companyId = undefined,
  additionalPartFields = undefined,
}: PartsFormV3ModalProps): JSX.Element => {
  const [tab, setTab] = useState(TABS.DETAILS);
  const [newPart, setNewPart] = useState<Part>(partData ?? initialStateNewPart);
  const [partUsageData, setPartUsageData] = useState<PartUsage>({
    ...(usageData ?? initialStatePartUsageData),
    ...(additionalPartFields ? { additionalPartFields } : {}),
  });
  const [returnTrackingRequired, setReturnTrackingRequired] =
    useState<boolean>(false);
  const [isUniqueExternalId, setIsUniqueExternalId] = useState<boolean>(true);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  // Part files only exist once the part has an id (edit / provider-usage modes);
  // the buyer add-part flow has no Usage tab and therefore no photos.
  const partFiles = usePartFiles(
    workOrderId,
    newPart.id,
    workOrderUserRole,
    Boolean(newPart.id),
  );

  useEffect(() => {
    // Not a provider and not all required fields are filled
    if (
      !isProvider &&
      (!newPart.externalId?.trim() ||
        !newPart.externalNumber?.trim() ||
        !newPart.description?.trim() ||
        !newPart.returnable?.trim() ||
        !newPart.trackingNumber?.trim())
    ) {
      setCanSubmit(false);
      return;
    }

    // Not a provider and externalId is not unique
    if (!isProvider && !isUniqueExternalId) {
      setCanSubmit(false);
      return;
    }

    // return tracking is required and returnable is yes but return tracking fields are not filled
    if (
      returnTrackingRequired &&
      newPart?.returnable === 'Yes' &&
      (!partUsageData.returnTracking?.trim() ||
        !partUsageData.returnCarrier?.trim() ||
        (partUsageData.returnCarrier === 'other' &&
          !partUsageData.returnCarrierOtherName?.trim()))
    ) {
      setCanSubmit(false);
      return;
    }

    if (
      partUsageData.usageCode === USAGE_ENUMS.USED &&
      !partUsageData.customerRetained &&
      newPart?.returnable === 'Yes'
    ) {
      setCanSubmit(false);
      return;
    }

    if (!newPart.returnInstruction) {
      setCanSubmit(false);
      return;
    }

    if (isProvider && !partUsageData?.usageCode) {
      setCanSubmit(false);
      return;
    }

    if (!validateAdditionalFields(
      partUsageData?.additionalPartFields,
      partUsageData?.additionalPartValues,
      partUsageData?.usageCode,
    )) {
      setCanSubmit(false);
      return;
    }

    setCanSubmit(true);
  }, [newPart, partUsageData, returnTrackingRequired, isUniqueExternalId]);

  const closeModal = () => {
    setOpenEditModal(false);
  };

  // Cancelling (or closing via X) rolls back any files uploaded this session.
  // Blocked entirely while an upload/delete is in flight so files never orphan.
  const handleCancel = async () => {
    if (partFiles.isBusy) {
      return;
    }
    // Await rollback so the modal stays open (buttons disabled via isBusy) until
    // session uploads are actually deleted — guarantees no orphaned files.
    await partFiles.rollback();
    closeModal();
  };

  const handleCreateAndUpdate = async () => {
    const companyIdValue = companyId ?? window?.context?.company?.id ?? 0;
    const statusId = getWorkorderStatusId(workOrderStatus);

    const payload = convertPartDataToV1Payload(newPart, partUsageData);
    let partResponse: PartV3 | undefined;

    if (addPart) {
      partResponse = await createPartApi(
        workOrderId,
        workOrderUserRole,
        companyIdValue,
        statusId,
        payload,
      );
    } else {
      partResponse = await updatePartApi(
        workOrderId,
        newPart.id,
        workOrderUserRole,
        companyIdValue,
        statusId,
        payload,
      );
    }

    if (!partResponse) {
      error('Failed to save part information, please try again later.');
      return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-unused-expressions
    window?._fnRedux?.dispatch({
      type: 'UPDATE_WORK_ORDER_PARTS',
      correlation_id: partsCorrelationId,
      payload: partResponse,
    });
    success('Part information updated.');
  };

  const onSave = async () => {
    if (partFiles.isBusy) {
      return;
    }
    if (!addPart) {
      // eslint-disable-next-line no-unused-expressions
      window?._amplitudeClient?.logEvent('Completed Parts Usage', {
        'Work Order ID': workOrderId,
        'Project Name': workOrderProjectName ?? '',
        'Usage Code': partUsageData?.usageCode,
        'Work Order Status': workOrderStatus ?? '',
        'Part ID': newPart?.externalId,
        'OEM Number ': newPart?.externalNumber,
        'Part Description': newPart?.description,
        'Retained by Customer': partUsageData?.customerRetained,
        Returnable: newPart?.returnable,
        'Return Tracking Number': partUsageData?.returnTracking,
        Carrier: newPart?.carrier,
        Note: partUsageData?.note,
      });
    }
    try {
      await handleCreateAndUpdate();
      // Fire the deferred deletes of previously-saved photos only once the part
      // save succeeds; session uploads are kept (no rollback on the save path).
      await partFiles.commit();
      // eslint-disable-next-line no-empty
    } catch (_) {
      error(
        `Failed to ${
          addPart ? 'save' : 'update'
        } part information, please try again later.`,
      );
    }
    closeModal();
  };

  const partsForm = isProvider ? (
    <Usage
      partData={newPart}
      usageData={partUsageData}
      setUsageData={setPartUsageData}
      usageCodes={usageCodes}
      returnTrackingRequired={returnTrackingRequired}
      setReturnTrackingRequired={setReturnTrackingRequired}
      isProvider={isProvider}
      partFiles={partFiles}
    />
  ) : (
    <>
      {!addPart && (
        <ActionTabs
          items={[
            {
              onClick: () => setTab(TABS.DETAILS),
              label: 'Details',
              active: tab === TABS.DETAILS,
            },
            {
              onClick: () => setTab(TABS.USAGE),
              label: 'Usage',
              active: tab === TABS.USAGE,
            },
          ]}
        />
      )}

      {tab === TABS.DETAILS && (
        <AddPart
          newPart={newPart}
          setNewPart={setNewPart}
          usageData={partUsageData}
          setUsageData={setPartUsageData}
          disableExternalId={!addPart}
          isUniqueExternalId={isUniqueExternalId}
          setIsUniqueExternalId={setIsUniqueExternalId}
        />
      )}

      {tab === TABS.USAGE && (
        <Usage
          partData={newPart}
          usageData={partUsageData}
          setUsageData={setPartUsageData}
          usageCodes={usageCodes}
          returnTrackingRequired={returnTrackingRequired}
          setReturnTrackingRequired={setReturnTrackingRequired}
          isProvider={isProvider}
          partFiles={partFiles}
        />
      )}
    </>
  );

  const getModalHeader = useCallback(
    () => (isProvider ? 'Enter part usage' : `${addPart ? 'Add' : 'Edit'} part`),
    [addPart, isProvider],
  );

  return (
    <Modal
      header={getModalHeader()}
      isOpen={openEditModal}
      onClose={handleCancel}
      size="medium"
      position="default"
      footerActions={[
        {
          label: 'Cancel',
          type: 'secondary',
          onClick: handleCancel,
          disabled: partFiles.isBusy,
        },
        {
          label: 'Save',
          type: 'primary',
          onClick: onSave,
          disabled: !canSubmit || partFiles.isBusy,
        },
      ]}
    >
      {partsForm}
    </Modal>
  );
};

PartsFormV3Modal.defaultProps = {
  usageCodes: USAGE_CODES,
  companyId: undefined,
  additionalPartFields: undefined,
};

export default PartsFormV3Modal;
